<script setup lang="ts">
/** POST /projects · PATCH /projects/{id} · POST /organizations. Project: name + description. Organisation: name + address slug, which follows the name until edited. */
import Dialog from '~/components/ui/Dialog.vue'
import Field from '~/components/ui/Field.vue'
import TextareaField from '~/components/ui/TextareaField.vue'
import Icon from '~/components/ui/Icon.vue'
import ActionButton from '~/components/ui/ActionButton.vue'

const props = defineProps<{ organization?: boolean; project?: { id: string; name: string; description?: string } | null }>()
defineEmits<{ close: []; submit: [payload: Record<string, unknown>] }>()
const name = ref(props.project?.name ?? '')
const description = ref(props.project?.description ?? '')
const slugify = (value: string) => value.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
const slugEdited = ref(false)
const slug = ref('')
const validSlug = computed(() => /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug.value))
watch(name, (value) => { if (!slugEdited.value) slug.value = slugify(value) })
watch(slug, (value, previous) => { if (value !== slugify(name.value) && value !== previous) slugEdited.value = true })
</script>

<template>
  <Dialog :title="organization ? 'New organisation' : project ? 'Edit project' : 'New project'" :meta="organization ? 'members · projects · billing' : project ? project.id : 'scopes data · models · deployments'" :width="480" @close="$emit('close')">
    <template #icon><Icon :name="organization ? 'people' : 'folder'" /></template>
    <Field v-model="name" label="Name" :placeholder="organization ? 'Acme Support' : 'Billing questions'" :hint="organization ? 'Shown in the organisation switcher' : 'Name shown in the project switcher'" />
    <Field v-if="organization" v-model="slug" label="Address" :hint="'/org/' + (slug || '…')" :error="slug && !validSlug ? 'Use lowercase letters, numbers and single hyphens' : undefined" />
    <TextareaField v-else v-model="description" label="Description" placeholder="What this project is for · optional" :rows="2" />
    <template #actions>
      <ActionButton label="Cancel" @click="$emit('close')" />
      <ActionButton :label="organization ? 'Create organisation' : project ? 'Save' : 'Create project'" variant="primary" :disabled="!name.trim() || (organization && !validSlug)" @click="$emit('submit', organization ? { name: name.trim(), slug } : { name: name.trim(), description: description.trim() })"><template #icon><Icon :name="project ? 'check' : 'plus'" /></template></ActionButton>
    </template>
  </Dialog>
</template>
